import React from "react";
import { ACTIVITY_KIND_NOUN } from "@atlas/shared";
import type { ActivityKind } from "@atlas/shared";
import { Button } from "./Button";

// ─── Bulk remove confirm (spec 26) ───────────────────────────────────────────
// Shared by the Activities select mode and the "Remove your N runs" action on
// Connected sources. Removal parks the rows (tombstone + archive copy), so the
// copy promises recovery honestly.

export function kindNoun(kind: ActivityKind | undefined, n: number): string {
  const noun = kind ? ACTIVITY_KIND_NOUN[kind] : ACTIVITY_KIND_NOUN.other;
  return n === 1 ? noun.one : noun.many;
}

export function BulkRemoveDialog({
  count,
  kind,
  pending,
  error,
  onConfirm,
  onClose,
}: {
  count: number;
  kind: ActivityKind | undefined;
  pending: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const noun = kindNoun(kind, count);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="bulk-remove-title"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !pending) onClose(); }}
    >
      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-strong)",
          borderRadius: "var(--radius-xl)",
          padding: 28,
          maxWidth: 440,
          width: "100%",
          boxShadow: "var(--shadow-xl)",
          minWidth: 0,
        }}
      >
        <h2 id="bulk-remove-title" style={{ font: "var(--type-h3)", color: "var(--text-primary)", margin: "0 0 12px" }}>
          Remove {count.toLocaleString("en-GB")} {noun} from Atlas?
        </h2>
        <p style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--text-secondary)", margin: "0 0 20px" }}>
          Peaks, trails and regions you matched on them stay on your record. Their traces come off
          the map. You can bring them back from Removed activities below.
        </p>
        {error && (
          <p style={{ fontSize: 13, color: "var(--status-danger)", margin: "0 0 14px" }}>{error}</p>
        )}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <Button variant="ghost" onClick={onClose} disabled={pending}>Cancel</Button>
          <Button variant="danger" onClick={onConfirm} disabled={pending || count === 0}>
            {pending ? "Removing…" : `Remove ${count.toLocaleString("en-GB")} ${noun}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
